import { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import ImageModal from '../components/ImageModal';
import { galleryImages, seoByRoute } from '../data/siteContent';

const fadeIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: (i = 1) => ({ opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.08 * i } }),
};

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const seo = seoByRoute['/gallery'];

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
  };

  const handleNext = () => {
    if (selectedIndex < galleryImages.length - 1) {
      const nextImage = galleryImages[selectedIndex + 1];
      setSelectedImage(nextImage);
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrev = () => {
    if (selectedIndex > 0) {
      const prevImage = galleryImages[selectedIndex - 1];
      setSelectedImage(prevImage);
      setSelectedIndex(selectedIndex - 1);
    }
  };

  return (
    <>
      <SEO {...seo} />
      <section className="section pt-32">
        <SectionHeading
          as="h1"
          eyebrow="Gallery"
          title="A visual taste of Duoley"
          description="Scenes from the farm, production floor, and product line-up. Hover to explore textures, colors, and moments from our day-to-day."
        />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {galleryImages.map((image, index) => (
            <motion.figure
              key={image.alt}
              className="group relative overflow-hidden rounded-[28px] bg-white shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-slate-300/70 transition-all duration-300 border border-slate-100 cursor-pointer"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              custom={index}
              whileHover={{ y: -6 }}
              onClick={() => handleImageClick(image, index)}
            >
              <div className="relative overflow-hidden h-72">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <figcaption className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 via-black/20 to-transparent p-6 text-sm text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="font-semibold">{image.alt}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <ImageModal
          isOpen={!!selectedImage}
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onNext={handleNext}
          onPrev={handlePrev}
          hasNext={selectedIndex !== null && selectedIndex < galleryImages.length - 1}
          hasPrev={selectedIndex !== null && selectedIndex > 0}
        />
      </section>

      <section className="mx-auto mt-32 max-w-6xl px-6">
        <motion.div
          className="rounded-[32px] bg-gradient-to-br from-primary to-primary/90 px-10 py-16 text-white relative overflow-hidden"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -ml-48 -mb-48" />

          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <div className="inline-block rounded-full bg-white/20 px-4 py-2 mb-6 border border-white/30">
              <p className="text-sm uppercase tracking-[0.35em] text-white font-semibold">Behind the scenes</p>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-white">Capturing moments of care</h2>
            <p className="mt-6 text-white leading-relaxed">
              Every image tells a story of dedication, from sunrise harvests to the careful hands that craft each product.
              These moments reflect our commitment to quality and community.
            </p>
          </div>
        </motion.div>
      </section>
    </>
  );
}
